import { Sliders01 } from "@untitledui/icons";
import { Select } from "@/components/base/select/select";
import type { Severity } from "@/components/safety-config/risk-groups";
import { SEVERITY_LABEL } from "@/components/safety-config/risk-groups";
import { cx } from "@/utils/cx";
import { FieldGroup } from "./FieldGroup";
import type { CustomRiskRequestForm } from "./RequestCustomRiskModal";

type Props = {
  form: CustomRiskRequestForm;
  onChange: (patch: Partial<CustomRiskRequestForm>) => void;
};

const SEVERITY_OPTIONS: { id: Severity; label: string }[] = (["low", "medium", "high", "critical"] as const).map(
  (id) => ({ id, label: SEVERITY_LABEL[id] }),
);

const RESPONSE_POLICY_OPTIONS = [
  { id: "remove_from_gmv_max_only", label: "Remove from GMV Max only" },
  { id: "send_warning_to_creator", label: "Send warning to creator" },
  {
    id: "send_warning_to_creator_and_remove_from_gmv_max",
    label: "Send warning to creator and remove from GMV Max",
  },
  { id: "request_metadata_edit", label: "Request metadata edit" },
  { id: "request_video_removal", label: "Request video removal" },
  { id: "end_linked_collaboration", label: "End linked collaboration" },
  { id: "end_creator_relationship", label: "End creator relationship" },
];

type ReviewMode = "auto_approve" | "require_review";

export function ConfigurationStep({ form, onChange }: Props) {
  const config = form.configuration;

  const handleReviewMode = (mode: ReviewMode) => {
    onChange({ configuration: { ...config, reviewMode: mode } });
  };

  return (
    <div className="flex gap-8">
      <div className="w-48 shrink-0 flex flex-col gap-3">
        <div className="bg-indigo-50 rounded-lg p-2 w-fit">
          <Sliders01 className="size-5 text-indigo-700" />
        </div>
        <div className="flex flex-col gap-2">
          <p className="text-sm font-semibold text-primary">Configuration</p>
          <p className="text-sm text-tertiary">
            Suggest how this risk should behave once live. You can change these later.
          </p>
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-5">
        <FieldGroup label="Suggested severity" description="How serious is this risk if a video triggers it?">
          <Select
            aria-label="Suggested severity"
            placeholder="Select a severity..."
            size="md"
            items={SEVERITY_OPTIONS}
            selectedKey={config.severity ?? null}
            onSelectionChange={(v) =>
              onChange({
                configuration: {
                  ...config,
                  severity: v as CustomRiskRequestForm["configuration"]["severity"],
                },
              })
            }
          >
            {(item) => <Select.Item id={item.id} label={item.label} />}
          </Select>
        </FieldGroup>

        <FieldGroup label="Suggested response policy" description="What should happen when a video triggers this risk?">
          <Select
            aria-label="Suggested response policy"
            placeholder="Select a response policy..."
            size="md"
            items={RESPONSE_POLICY_OPTIONS}
            selectedKey={config.responsePolicy ?? null}
            onSelectionChange={(v) =>
              onChange({
                configuration: {
                  ...config,
                  responsePolicy: v as CustomRiskRequestForm["configuration"]["responsePolicy"],
                },
              })
            }
          >
            {(item) => <Select.Item id={item.id} label={item.label} />}
          </Select>
        </FieldGroup>

        <FieldGroup
          label="Review mode"
          description="When a risk is detected, auto-approve it and run the response policy immediately — or require review so someone on your team confirms it first."
        >
          {/* Custom tab toggle: bg-gray-100 container, white active tab, 13px font */}
          <div className="flex gap-1 rounded-[10px] border border-secondary bg-gray-100 p-1 w-full">
            {(["auto_approve", "require_review"] as const).map((mode) => {
              const isActive = (config.reviewMode ?? "auto_approve") === mode;
              return (
                <button
                  key={mode}
                  type="button"
                  onClick={() => handleReviewMode(mode)}
                  className={cx(
                    "flex-1 h-9 rounded-[6px] px-3 py-2 font-semibold transition-colors",
                    "flex items-center justify-center text-[13px]",
                    isActive
                      ? "bg-white text-[#181D27] shadow-xs"
                      : "bg-transparent text-quaternary hover:text-tertiary",
                  )}
                >
                  {mode === "auto_approve" ? "Auto-approve" : "Require review"}
                </button>
              );
            })}
          </div>
        </FieldGroup>
      </div>
    </div>
  );
}
